import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, RevealLines, RevealWordsOnView, easeSmooth } from "@/lib/motion";
import Marquee from "@/components/site/Marquee";
import { ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/13203184/pexels-photo-13203184.jpeg";
const IMG_INTERIOR =
  "https://images.unsplash.com/photo-1564078516393-cf04bd966897?auto=format&fit=crop&w=1600&q=80";
const IMG_MODERN =
  "https://images.unsplash.com/photo-1679364297777-1db77b6199be?auto=format&fit=crop&w=1600&q=80";
const IMG_SITE =
  "https://images.unsplash.com/photo-1647735282077-c12699af40be?auto=format&fit=crop&w=1600&q=80";
const IMG_CEILING =
  "https://images.pexels.com/photos/31735042/pexels-photo-31735042.jpeg";
const IMG_INDUSTRIAL =
  "https://images.unsplash.com/photo-1590274853856-f22d5ee3d228?auto=format&fit=crop&w=1600&q=80";

const chapters = [
  { n: "01", title: "Precision as principle", body: "Every joint, seam and shadow is drawn twice — once on paper, once on site. We treat tolerances the way jewellers treat stone." },
  { n: "02", title: "Materials with memory", body: "Warm oak, hand-troweled plaster, patinated brass. We source materials that age with grace, so the building writes its own quiet history." },
  { n: "03", title: "Craft is a conversation", body: "Our tradespeople, architects and clients share one table. Nothing is delegated blindly — the building improves at every seat." },
  { n: "04", title: "London, Ontario — outward", body: "From heritage streets to industrial parks, we build across South-Western Ontario. Local footing, uncompromising standard." },
];

const services = [
  { tag: "Residential", title: "Custom Homes & Estates", body: "Ground-up builds and gut renovations for private clients who care where the light falls." },
  { tag: "Commercial", title: "Retail, Office & Hospitality", body: "Interiors and shells that read as a signature — precise, warm, functional." },
  { tag: "Industrial", title: "Warehouses & Facilities", body: "High-capacity envelopes, engineered floors, tight schedules, zero drama." },
  { tag: "Heritage", title: "Restoration & Additions", body: "Careful surgical work on London’s older stock — respectful, structural, luminous." },
];

const projects = [
  { name: "Atrium Kitchen & Bar", meta: "Residential · 2024", img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/7hexdmyw_PHOTO-2026-05-23-10-27-00%20%283%29.jpg", size: "big" },
  { name: "Marble Galley Kitchen", meta: "Residential · 2025", img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/lvvtqn4x_PHOTO-2026-05-23-10-27-00.jpg", size: "tall" },
  { name: "Diagonal Basement Suite", meta: "Residential · 2024", img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/9ajn1v3v_PHOTO-2026-05-23-10-27-00%20%282%29.jpg", size: "wide" },
  { name: "Quartz Island Renovation", meta: "Residential · 2025", img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/19qd0i05_PHOTO-2026-05-23-10-27-00%20%281%29.jpg", size: "small" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.75]);

  return (
    <main data-testid="home-page" className="relative">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] w-full overflow-hidden grain"
        data-testid="hero-section"
      >
        <motion.div
          style={{ scale: heroScale, y: heroY }}
          className="absolute inset-0 will-change-transform"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: easeSmooth }}
        >
          <img
            src={HERO_IMG}
            alt="Yach Construction — luxury architectural build"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />

        {/* Giant background logo watermark */}
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center z-[1]"
          aria-hidden
        >
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/2iaunrlz_YACH_Construction_Print_HighRes_300DPI.webp"
            alt=""
            className="w-[92vw] md:w-[62vw] max-w-none opacity-[0.14] select-none"
            style={{ mixBlendMode: "screen" }}
          />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-24 md:pt-28">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1, ease: easeSmooth }}
            className="flex items-center justify-between text-[#FAF9F6]/80 font-grotesk text-[11px] uppercase tracking-[0.28em]"
          >
            <span>Est. Ontario · Since 2019</span>
            <span className="hidden md:inline">Portfolio № 24 — Winter Edition</span>
            <span>Scroll ↓</span>
          </motion.div>
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-[18vh] md:pt-[22vh]">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 1, ease: easeSmooth }}
            className="mb-6 md:mb-8"
          >
            <span className="inline-flex items-center gap-3 font-grotesk text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#FAF9F6]/85">
              <span className="w-6 h-px bg-[#FAF9F6]/60" />
              Renovate, Rejuvenate &amp; Rediscover your space
            </span>
          </motion.div>
          <RevealLines
            className="hero-display text-[#FAF9F6]"
            lines={[
              <>Building</>,
              <><span className="hero-italic">quiet</span> monuments</>,
              <>in London, ON.</>,
            ]}
            delayBase={0.35}
            stagger={0.18}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 1, ease: easeSmooth }}
            className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end"
          >
            <p className="md:col-span-5 text-[#FAF9F6]/85 font-grotesk text-[15px] md:text-[16px] leading-[1.55] max-w-xl">
              A design-led construction studio crafting residential estates, commercial
              interiors and industrial envelopes across South-Western Ontario. Fewer
              projects, deeper craft.
            </p>
            <div className="md:col-span-4 md:col-start-9 flex flex-wrap gap-4">
              <Link to="/projects" className="btn-sharp" data-testid="hero-cta-portfolio">
                <span>View Portfolio</span>
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="btn-ghost"
                style={{ color: "#FAF9F6", borderColor: "#FAF9F6" }}
                data-testid="hero-cta-contact"
              >
                <span>Start an Enquiry</span>
                <span>→</span>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Bottom stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 1, ease: easeSmooth }}
          className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/15 bg-black/25 backdrop-blur-sm"
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-[#FAF9F6]">
            {[
              { k: "2019", v: "Established in Ontario" },
              { k: "80+", v: "Projects delivered" },
              { k: "3", v: "Sectors: Res · Com · Ind" },
              { k: "100%", v: "On-time handovers, 2024" },
            ].map((s, i) => (
              <div key={i} className="flex items-baseline gap-3">
                <span className="font-display text-3xl md:text-4xl">{s.k}</span>
                <span className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-white/70">
                  {s.v}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee items={["Residential", "Commercial", "Industrial", "Heritage", "Interiors"]} />

      {/* STUDIO INTRO */}
      <section className="relative py-28 md:py-40 px-6 md:px-10 max-w-[1600px] mx-auto overflow-hidden" data-testid="intro-section">
        {/* Watermark logo behind */}
        <div className="pointer-events-none absolute -right-[10vw] top-1/2 -translate-y-1/2 select-none" aria-hidden>
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/2iaunrlz_YACH_Construction_Print_HighRes_300DPI.webp"
            alt=""
            className="w-[60vw] md:w-[42vw] opacity-[0.09]"
            style={{ mixBlendMode: "multiply" }}
          />
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <span className="eyebrow">Yach — Est. 2019</span>
              <div className="mt-8 font-grotesk text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> London, Ontario, Canada
              </div>
              <div className="mt-3 font-grotesk text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] flex items-center gap-2">
                <Phone className="w-3.5 h-3.5" /> +1 519-702-9417
              </div>
              <div className="mt-3 font-grotesk text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Yachconstruction1@gmail.com
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <RevealWordsOnView
              text="Yach Construction is a full-spectrum builder for residential, commercial and industrial work — a studio the size of a large family, with the reach of a general contractor and the discipline of an atelier. We take on fewer projects, invest more attention, and hand over buildings that feel inevitable."
              className="font-display text-3xl md:text-5xl leading-[1.15] tracking-tight text-[#1A1A1A]"
              stagger={0.02}
            />
          </div>
        </div>
      </section>

      {/* FOUNDER — team of one */}
      <section className="bg-[#1A1A1A] text-[#FAF9F6] relative overflow-hidden" data-testid="founder-section">
        {/* Background watermark */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center select-none" aria-hidden>
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/2iaunrlz_YACH_Construction_Print_HighRes_300DPI.webp"
            alt=""
            className="w-[85vw] md:w-[55vw] opacity-[0.12]"
          />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <Reveal>
              <div className="clip-frame aspect-[4/5] border border-white/15">
                <img
                  src="https://images.unsplash.com/photo-1647735282077-c12699af40be?auto=format&fit=crop&w=1200&q=80"
                  alt="Yeeshoo Chopra — Founder"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pl-10">
            <Reveal>
              <span className="eyebrow" style={{ color: "#EAE7E0" }}>The Studio</span>
              <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
                Founded in 2019 <br />by <em className="hero-italic">Yeeshoo Chopra.</em>
              </h2>
              <p className="mt-8 max-w-xl font-grotesk text-[15px] leading-[1.7] text-white/70">
                Yach Construction was founded in London, Ontario in 2019 with a single
                idea — that a building is a document of the care that made it. Six years
                on, that idea still runs every meeting: draw twice, source honestly,
                deliver on schedule.
              </p>
              <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6 border-t border-white/15 pt-8 max-w-xl">
                {[
                  ["80+", "Projects built"],
                  ["6", "Years in practice"],
                  ["100%", "On-time in 2024"],
                  ["24", "Craftsmen on staff"],
                  ["3", "Sectors served"],
                  ["1", "Standard, no exceptions"],
                ].map(([k, v]) => (
                  <div key={v}>
                    <div className="font-display text-3xl md:text-4xl">{k}</div>
                    <div className="mt-1 font-grotesk text-[10px] uppercase tracking-[0.22em] text-white/50">
                      {v}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO — numbered chapters */}
      <section className="relative bg-[#EAE7E0] border-y border-[#D9D4C7]" data-testid="manifesto-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="flex items-end justify-between mb-16">
            <div>
              <span className="eyebrow">The Manifesto</span>
              <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
                Four rules we <em className="hero-italic">refuse</em> to bend.
              </h2>
            </div>
            <div className="hidden md:block font-mono-tab text-xs uppercase tracking-[0.28em] text-[#4A4A4A]">
              № 01 — 04
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="border-t border-[#1A1A1A]/20 pt-8 group">
                  <div className="flex items-baseline justify-between mb-5">
                    <span className="font-display text-6xl md:text-7xl leading-none">{c.n}</span>
                    <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#4A4A4A]">Chapter</span>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl mb-4 tracking-tight">{c.title}</h3>
                  <p className="text-[#4A4A4A] font-grotesk text-[15px] leading-[1.6] max-w-lg">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SPOTLIGHT / PROJECTS BENTO */}
      <section className="py-24 md:py-32 px-6 md:px-10 max-w-[1600px] mx-auto" data-testid="featured-projects">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="eyebrow">Selected Work</span>
            <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
              Recent <em className="hero-italic">landmarks.</em>
            </h2>
          </div>
          <Link to="/projects" className="btn-ghost" data-testid="view-all-projects">
            <span>View full portfolio</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6 auto-rows-[220px] md:auto-rows-[260px]">
          <ProjectTile p={projects[0]} className="col-span-12 md:col-span-8 row-span-2" />
          <ProjectTile p={projects[1]} className="col-span-6 md:col-span-4 row-span-2" />
          <ProjectTile p={projects[2]} className="col-span-6 md:col-span-7 row-span-1" />
          <ProjectTile p={projects[3]} className="col-span-6 md:col-span-5 row-span-1" />
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#1A1A1A] text-[#FAF9F6] relative overflow-hidden" data-testid="services-preview">
        <div className="pointer-events-none absolute -left-[10vw] bottom-[-10vh] select-none" aria-hidden>
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/2iaunrlz_YACH_Construction_Print_HighRes_300DPI.webp"
            alt=""
            className="w-[55vw] md:w-[38vw] opacity-[0.10]"
          />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="flex items-end justify-between mb-14">
            <div>
              <span className="eyebrow" style={{ color: "#EAE7E0" }}>What we build</span>
              <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
                Four practices, <em className="hero-italic">one standard.</em>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-white/15">
            {services.map((s, i) => (
              <Reveal key={s.tag} delay={i * 0.06}>
                <div
                  className={`p-8 md:p-10 border-b border-white/15 ${
                    i % 2 === 0 ? "md:border-r md:border-white/15" : ""
                  } group hover:bg-white/[0.03] transition-colors duration-500`}
                >
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-white/60">
                      {String(i + 1).padStart(2, "0")} · {s.tag}
                    </span>
                    <ArrowUpRight className="w-5 h-5 text-white/50 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl mb-4 leading-tight">{s.title}</h3>
                  <p className="text-white/70 font-grotesk text-[15px] leading-[1.6] max-w-lg">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-36 px-6 md:px-10 max-w-[1600px] mx-auto text-center overflow-hidden" data-testid="home-cta">
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center select-none" aria-hidden>
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/2iaunrlz_YACH_Construction_Print_HighRes_300DPI.webp"
            alt=""
            className="w-[80vw] md:w-[50vw] opacity-[0.10]"
            style={{ mixBlendMode: "multiply" }}
          />
        </div>
        <Reveal className="relative">
          <span className="eyebrow justify-center">Begin</span>
          <h2 className="mt-8 font-display text-6xl md:text-[8vw] leading-[0.9] tracking-tighter">
            Ready to <em className="hero-italic">build?</em>
          </h2>
          <p className="mt-8 max-w-xl mx-auto font-grotesk text-[15px] text-[#4A4A4A] leading-[1.7]">
            Book a discovery conversation. We’ll review your site, budget, and ambitions —
            then propose a build path that respects all three.
          </p>
          <div className="mt-10 flex items-center justify-center gap-6 flex-wrap">
            <Link to="/contact" className="btn-sharp" data-testid="cta-start">
              <span>Start an Enquiry</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a href="tel:+15197029417" className="btn-ghost" data-testid="cta-call">
              <span>Call +1 519-702-9417</span>
              <span>→</span>
            </a>
          </div>
        </Reveal>
      </section>
    </main>
  );
}

function ProjectTile({ p, className = "" }) {
  return (
    <Reveal className={`clip-frame group relative ${className}`}>
      <Link to="/projects" className="block absolute inset-0" data-testid={`tile-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
        <img src={p.img} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-90" />
        <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-[#FAF9F6]">
          <div className="flex items-end justify-between">
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-white/70 mb-2">{p.meta}</div>
              <div className="font-display text-2xl md:text-3xl leading-tight">{p.name}</div>
            </div>
            <ArrowUpRight className="w-6 h-6 opacity-70 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
          </div>
        </div>
      </Link>
    </Reveal>
  );
}
